import { Button } from '../../ui/Control'

/**
 * What can be *done* on the board, as visible controls floating over it.
 *
 * **This is where the rule "a gesture is a shortcut, never the only way" is actually kept.** Every
 * gesture the surface understands has its counterpart here or in the panel beside the board:
 * pinching has `+` and `−`, dragging an end onto a card has *connect*, and taking something off with
 * the Delete key has *Take off the board* in the panel. A gesture nobody can see is a gesture nobody can be told about, and on a board that
 * would leave most of the area undiscoverable.
 *
 * **Two bars and no third.** What is on this one is *doing* — making something, and looking around.
 * What the selected thing *is* — its colour, its marking, how a line runs — used to be a third bar
 * here and is now `Properties`, beside the board: a bar has room for pictures and not for words, so
 * every property was an icon somebody had to try, and it grew and shrank with the selection, which
 * moved the controls next to it under the pointer.
 *
 * It floats on `control--floating`, which the design system already has for exactly this: a control
 * that has to read against something it does not control.
 */
export interface ToolbarProps {
  /** `false` on a phone. Only looking, moving about and finding are left. */
  readonly rearrangeable: boolean
  readonly connecting: boolean
  readonly canUndo: boolean
  readonly onPutDown: () => void
  readonly onSticky: () => void
  readonly onTextBlock: () => void
  readonly onFrame: () => void
  readonly onConnect: () => void
  readonly onZoom: (factor: number) => void
  readonly onFitAll: () => void
  readonly onFind: () => void
  readonly onUndo: () => void
}

/**
 * The colours a board offers.
 *
 * A small, named palette rather than a colour picker: six things somebody can tell apart at a glance
 * beat sixteen million they have to remember. They are named here and turned into actual colours in
 * `styles/desk.css`, so a scheme change is a stylesheet and not a migration.
 */
export const COLOURS: readonly { readonly id: string; readonly label: string }[] = [
  { id: 'amber', label: 'Amber' },
  { id: 'rose', label: 'Rose' },
  { id: 'teal', label: 'Teal' },
  { id: 'violet', label: 'Violet' },
  { id: 'sky', label: 'Sky' },
  { id: 'lime', label: 'Lime' },
]

export function Toolbar({
  rearrangeable,
  connecting,
  canUndo,
  onPutDown,
  onSticky,
  onTextBlock,
  onFrame,
  onConnect,
  onZoom,
  onFitAll,
  onFind,
  onUndo,
}: ToolbarProps) {
  return (
    <div className="board__tools" role="toolbar" aria-label="What to do on this board">
      {rearrangeable && (
        <div className="board__group">
          {/*
            Named after what it *does*, which "Put something down" did not say: beside four controls
            that each make something new, it read as the heading of the bar rather than as the one
            thing here that reaches for something you already have.
          */}
          <Button tone="floating" icon="library" onClick={onPutDown}>
            {ADD_EXISTING}
          </Button>
          <Button
            tone="floating"
            icon="sticky"
            iconOnlyLabel="New sticky note"
            onClick={onSticky}
          />
          <Button
            tone="floating"
            icon="notes"
            iconOnlyLabel="New text block"
            onClick={onTextBlock}
          />
          <Button tone="floating" icon="frame" iconOnlyLabel="New frame" onClick={onFrame} />
          <Button
            tone="floating"
            icon="connect"
            iconOnlyLabel={connecting ? 'Stop connecting' : 'Connect two things'}
            aria-pressed={connecting}
            onClick={onConnect}
          />
        </div>
      )}

      <div className="board__group board__group--view">
        <Button
          tone="floating"
          icon="zoomOut"
          iconOnlyLabel="Zoom out"
          onClick={() => {
            onZoom(1 / ZOOM_STEP)
          }}
        />
        <Button
          tone="floating"
          icon="zoomIn"
          iconOnlyLabel="Zoom in"
          onClick={() => {
            onZoom(ZOOM_STEP)
          }}
        />
        <Button tone="floating" icon="fit" iconOnlyLabel="Fit everything in" onClick={onFitAll} />
        <Button tone="floating" icon="search" iconOnlyLabel="Find on this board" onClick={onFind} />
        {rearrangeable && (
          <Button
            tone="floating"
            icon="undo"
            iconOnlyLabel="Undo the last change"
            disabled={!canUndo}
            onClick={onUndo}
          />
        )}
      </div>
    </div>
  )
}

/** How much one press of `+` or `−` changes the zoom. A quarter is a step somebody can follow. */
export const ZOOM_STEP = 1.25

/**
 * What the control that lays existing content down is called.
 *
 * In one place, because the empty board offers the same thing and the two must not drift apart. It
 * names *where the things come from* rather than the gesture: everything else in this bar makes
 * something that did not exist a moment ago, and this one does not.
 */
export const ADD_EXISTING = 'Add from MyLib'
